import { useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import { useBoolean } from 'src/hooks/use-boolean';
import { useTypewriter } from 'src/hooks/use-typewriter';

// ----------------------------------------------------------------------
// Styles - Start

const REVIEW_CARD_SX = {
  backgroundColor: alpha('#FFFFFF', 0.1),
  p: 2,
  borderRadius: 1.5,
  border: `1px dashed ${alpha('#919EAB', 0.1)}`,
};

const TYPEWRITER_CURSOR_SX = {
  display: 'inline-block',
  width: 2,
  height: '1em',
  marginLeft: 0.5,
  verticalAlign: 'text-bottom',
  animation: 'typewriter-blink 0.7s step-end infinite',
  '@keyframes typewriter-blink': {
    '50%': { opacity: 0 },
  },
};
// Styles - End

// Step 1: intro line, then project name on its own line, then "Does it seem right?"
function getProjectNameParts(projectName) {
  return {
    before: 'It looks like the project name is:\n\n',
    name: `"${projectName}"`,
    after: '\n\nDoes it seem right?',
  };
}

// Step 2: intro line, then address fields one per line, then "Does it seem right?"
function getAddressMessage({ street_address, city, state, zipcode }) {
  const lines = ['It looks like the project address is:\n'];
  if (street_address != null && String(street_address).trim()) {
    lines.push(`Street Address: ${street_address}`);
  }
  if (city != null && String(city).trim()) lines.push(`City: ${city}`);
  if (state != null && String(state).trim()) lines.push(`State: ${state}`);
  if (zipcode != null && String(zipcode).trim()) lines.push(`Zip Code: ${zipcode}`);
  if (lines.length === 1) lines.push('No address details');
  lines.push('', 'Does it seem right?');
  return lines.join('\n');
}

// ----------------------------------------------------------------------

export default function ProjectReviewDialog({ dialog, extractedData, onConfirm, onBack }) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  // Default form fields
  const defaultFormFields = {
    project_name: extractedData?.project_name ?? '',
    street_address: extractedData?.street_address ?? '',
    city: extractedData?.city ?? '',
    state: extractedData?.state ?? '',
    zipcode: extractedData?.zipcode ?? '',
  };

  // Form state
  const [form, setForm] = useState(defaultFormFields);

  // Stepper
  const [step, setStep] = useState(1);

  // Correctness flags
  const isProjectNameCorrect = useBoolean(true);
  const isProjectDetailsCorrect = useBoolean(true);

  // Typewriters - Start
  // Step 1: Project name review with typewriter
  const projectNameParts = getProjectNameParts(defaultFormFields.project_name);

  const twBefore = useTypewriter({
    open: dialog.value && step === 1,
    text: projectNameParts.before,
    speedMs: 30,
  });

  const twName = useTypewriter({
    open: dialog.value && step === 1 && twBefore.done,
    text: projectNameParts.name,
    speedMs: 30,
  });

  const twAfter = useTypewriter({
    open: dialog.value && step === 1 && twName.done,
    text: projectNameParts.after,
    speedMs: 30,
  });

  // Step 2: Address review with typewriter
  const addressMessage = getAddressMessage(defaultFormFields);
  const typewriterAddress = useTypewriter({
    open: dialog.value && step === 2,
    text: addressMessage,
    speedMs: 30,
  });

  // Typewriters - End

  // Handlers - Start
  const handleLooksGood = () => {
    if (step === 1) {
      setForm((p) => ({ ...p, project_name: (p.project_name || '').trim() }));
      setStep(2);
      isProjectNameCorrect.onTrue();
    } else {
      setForm((p) => ({
        ...p,
        project_name: (p.project_name || '').trim(),
        street_address: (p.street_address || '').trim(),
        city: (p.city || '').trim(),
        state: (p.state || '').trim(),
        zipcode: (p.zipcode || '').trim(),
      }));
      isProjectDetailsCorrect.onTrue();
      onConfirm(form);
      onBack?.();
    }
  };

  const handleEdit = () => {
    if (step === 1) isProjectNameCorrect.onFalse();
    else isProjectDetailsCorrect.onFalse();
  };
  // Handlers - End

  // Styles - Start
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: alpha('#ffffff', 0.1),
      '& fieldset': { borderColor: alpha(theme.palette.common.white, 0.14) },
      '&:hover fieldset': { borderColor: alpha(theme.palette.common.white, 0.22) },
      '&.Mui-focused fieldset': { borderColor: alpha(primary, 0.65) },
    },
    '& input': { color: alpha(theme.palette.common.white, 0.92) },
  };

  const labelSx = { fontWeight: 400, fontSize: 12, color: theme.palette.common.white };

  // Styles - End

  return (
    <Dialog
      open={dialog.value}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        dialog.onFalse();
      }}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          maxWidth: 400,
          borderRadius: 5,
          px: 2,
          overflow: 'hidden',
          color: 'common.white',
          background: '#000000',
          border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: alpha(theme.palette.grey[900], 0.6),
            backdropFilter: 'blur(4px)',
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.25, pt: 2.25, px: 2.25 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.25}>
          <Typography sx={{ fontWeight: 700, fontSize: 22, color: theme.palette.common.white }}>
            Project Details
          </Typography>
          <Iconify
            icon="iconamoon:close-fill"
            width={24}
            height={24}
            onClick={onBack}
            sx={{ cursor: 'pointer' }}
          />
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, px: 2.25, pb: 2.25 }}>
        <Stack spacing={2}>
          {/* Step 1: Project name review with typewriter */}
          {step === 1 && (
            <Stack sx={REVIEW_CARD_SX}>
              <Typography
                component="span"
                sx={{
                  fontWeight: 400,
                  fontSize: 20,
                  color: theme.palette.common.white,
                  whiteSpace: 'pre-line',
                  '& .typewriter-cursor': {
                    ...TYPEWRITER_CURSOR_SX,
                    backgroundColor: theme.palette.common.white,
                  },
                }}
              >
                {twBefore.value}

                <Typography
                  component="span"
                  sx={{
                    fontWeight: 700,
                    fontSize: 20,
                    color: theme.palette.primary.white,
                    whiteSpace: 'pre-line',

                    '& .typewriter-cursor': {
                      ...TYPEWRITER_CURSOR_SX,
                      backgroundColor: theme.palette.common.white,
                    },
                  }} // 👈 bold project name
                >
                  {twName.value}
                </Typography>

                {twAfter.value}

                {!(twBefore.done && twName.done && twAfter.done) && (
                  <span className="typewriter-cursor" />
                )}
              </Typography>
            </Stack>
          )}

          {step === 1 && !isProjectNameCorrect.value && (
            <Stack>
              <Typography sx={labelSx}>Project name</Typography>
              <TextField
                placeholder="Project name"
                value={form.project_name}
                onChange={(e) => setForm((p) => ({ ...p, project_name: e.target.value }))}
                fullWidth
                sx={inputSx}
              />
            </Stack>
          )}

          {/* Step 2: Address review with typewriter */}
          {step === 2 && (
            <Stack sx={REVIEW_CARD_SX}>
              <Typography
                component="span"
                sx={{
                  fontWeight: 400,
                  fontSize: 20,
                  color: theme.palette.common.white,
                  whiteSpace: 'pre-line',
                  '& .typewriter-cursor': {
                    ...TYPEWRITER_CURSOR_SX,
                    backgroundColor: theme.palette.common.white,
                  },
                }}
              >
                {typewriterAddress.value}
                {!typewriterAddress.done && <span className="typewriter-cursor" />}
              </Typography>
            </Stack>
          )}

          {step === 2 && !isProjectDetailsCorrect.value && (
            <Stack spacing={1.5}>
              <Stack>
                <Typography sx={labelSx}>Street Address</Typography>
                <TextField
                  placeholder="Street Address"
                  value={form.street_address}
                  onChange={(e) => setForm((p) => ({ ...p, street_address: e.target.value }))}
                  fullWidth
                  sx={inputSx}
                />
              </Stack>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Stack flex={1}>
                  <Typography sx={labelSx}>City</Typography>
                  <TextField
                    placeholder="City"
                    value={form.city}
                    onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                    fullWidth
                    sx={inputSx}
                  />
                </Stack>
                <Stack flex={1}>
                  <Typography sx={labelSx}>State</Typography>
                  <TextField
                    placeholder="State"
                    value={form.state}
                    onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))}
                    fullWidth
                    sx={inputSx}
                  />
                </Stack>
                <Stack flex={1}>
                  <Typography sx={labelSx}>Zip Code</Typography>
                  <TextField
                    placeholder="Zip Code"
                    value={form.zipcode}
                    onChange={(e) => setForm((p) => ({ ...p, zipcode: e.target.value }))}
                    fullWidth
                    sx={inputSx}
                  />
                </Stack>
              </Stack>
            </Stack>
          )}

          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            sx={{
              backgroundColor: alpha('#FFFFFF', 0.1),
              borderRadius: 1.5,
              border: `1px dashed ${alpha('#919EAB', 0.1)}`,
              width: 'fit-content',
            }}
          >
            <IconButton aria-label="looks good" onClick={handleLooksGood}>
              <Iconify
                icon="heroicons-outline:thumb-up"
                color={theme.palette.common.white}
                width={24}
                height={24}
              />
            </IconButton>
            {((step === 1 && isProjectNameCorrect.value) ||
              (step === 2 && isProjectDetailsCorrect.value)) && (
              <IconButton aria-label="edit" onClick={handleEdit}>
                <Iconify
                  icon="heroicons-outline:thumb-down"
                  color={theme.palette.common.white}
                  width={24}
                  height={24}
                />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

ProjectReviewDialog.propTypes = {
  dialog: PropTypes.shape({
    value: PropTypes.bool,
    onFalse: PropTypes.func,
  }),
  extractedData: PropTypes.object,
  onConfirm: PropTypes.func.isRequired,
  onBack: PropTypes.func,
};
